import {
  IonCard,
  IonCardContent,
  IonIcon,
  IonImg,
  IonLabel,
  IonPage,
  useIonViewDidEnter,
} from '@ionic/react';
import classNames from 'classnames';
import { chevronBackOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import CirclePepper from '@/assets/imgs/circle-pepper.svg';
import Banner from '@/components/common/Banner';
import { BannerType } from '@/components/common/Banner/Banner';
import Button from '@/components/common/Button';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { ClientTaxonomyEvents } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import Faq from '@/pages/Faq/Faq';
import { saveMerchantData } from '@/redux/merchant/merchant';
import { getMerchants } from '@/redux/operation/operationsAPI';
import {
  CommercialAgent,
  Merchant,
} from '@/redux/operation/operationsInterface';
import { Analytics } from '@/services/analytics';
import { RootState } from '@/store';

import styles from './Contact.module.scss';

export default function Contact() {
  const [commercialAgent, setCommercialAgent] = useState<CommercialAgent>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // Al entrar con hash en la URL (#tel_contact), se hace scroll hasta
  // ese elemento.
  useIonViewDidEnter(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  const merchantInfo = useSelector(
    (state: RootState) => state.merchantData.data,
  );

  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const [isMobile, setIsMobile] = useState(!isTablet && !isDesktop);

  // objeto por defecto de comercial agent
  const defaultCommercialAgent: CommercialAgent = {
    name: 'Pepper',
    surname: 'Group',
    phones: ['91 216 12 12'],
    email: 'consultascomercial@peppergroup.es',
    call_center_phone: '91 216 12 12',
  };
  useEffect(() => {
    setIsMobile(!isTablet && !isDesktop);
  }, [isTablet, isDesktop]);

  const merchantAndCommercialApiCall = async () => {
    try {
      let merchantResponse: Merchant;
      if (merchantInfo) {
        merchantResponse = merchantInfo;
      } else {
        merchantResponse = await getMerchants();
        dispatch(saveMerchantData(merchantResponse));
      }
      const commercialAgentData: CommercialAgent = getCommercialAgent(merchantResponse);
      setCommercialAgent(commercialAgentData);
    } catch (err) {}
  };
  const getCommercialAgent = (data: Merchant): CommercialAgent => {
    if (!data || !data.commercial_agent) {
      return defaultCommercialAgent;
    }

    const commercialAgentData = data.commercial_agent;

    return {
      name: commercialAgentData.name || defaultCommercialAgent.name,
      surname: commercialAgentData.surname || defaultCommercialAgent.surname,
      phones:
        commercialAgentData.phones.length > 0
          ? commercialAgentData.phones
          : defaultCommercialAgent.phones,
      email: commercialAgentData.email || defaultCommercialAgent.email,
      call_center_phone:
        commercialAgentData.call_center_phone
        || defaultCommercialAgent.call_center_phone,
    };
  };

  const handleCall = () => {
    Analytics.registerAnalyticsEvent({
      [ClientTaxonomyEvents.CONTACT]: { type: 'PHONE' },
    });
    window.open(`tel:${commercialAgent?.phones[0]}`);
  };

  const handleEmail = () => {
    Analytics.registerAnalyticsEvent({
      [ClientTaxonomyEvents.CONTACT]: { type: 'EMAIL' },
    });
    window.open(`mailto:${commercialAgent?.email}`);
  };

  useEffect(() => {
    merchantAndCommercialApiCall();
  }, []);

  return (
    <IonPage className={styles.contactMainWrapper}>
      <Banner type={BannerType.HELP} />
      {isMobile && (
        <div className={styles.pageContactTitle}>
          <IonIcon
            md={chevronBackOutline}
            className={styles.pageContactTitleIcon}
            onClick={() => {
              history.goBack();
            }}
          />
          <IonLabel className={styles.pageContactTitleInfo}>Contacto</IonLabel>
        </div>
      )}
      <div className={styles.mainWrapper}>
        {isTablet && (
          <div className={styles.helpWrapper}>
            <Faq />
          </div>
        )}

        <div className={styles.contactWrapper}>
          <div className={styles.pageContact}>
            <div className={styles.pageContactCards}>
              <div
                className={classNames(
                  styles.pageContactCardWrapper,
                  styles.commercialCard,
                )}
                id="tel_contact"
              >
                <div className={styles.contactTitles}>
                  <IonLabel className={styles.contactText}>
                    Contacta con tu comercial
                  </IonLabel>
                  <IonLabel className={styles.descriptionText}>
                    Nuestro equipo está siempre dispuesto a ayudarte.
                  </IonLabel>
                </div>
                <div className={styles.pageContactCardCommercial}>
                  <IonCard className={styles.cardComercial}>
                    <IonCardContent className={styles.comercialContentWrapper}>
                      <div className={styles.comercialContent}>
                        <IonImg
                          src={CirclePepper}
                          className={styles.circlePepper}
                        />
                        <IonLabel className={styles.comercialContentName}>
                          {`${commercialAgent?.name} ${commercialAgent?.surname}`}
                        </IonLabel>
                      </div>
                      <div className={styles.comercialContent}>
                        <IonLabel className={styles.comercialContentPhone}>
                          al
                          {' '}
                          <span>{commercialAgent?.phones[0]}</span>
                        </IonLabel>
                        <Button
                          className={styles.buttonCallCommercial}
                          id="button-call-commercial"
                          onClick={() => {
                            handleCall();
                          }}
                        >
                          <p>Llámanos</p>
                        </Button>
                      </div>
                      <div className={styles.comercialContent}>
                        <IonLabel className={styles.comercialContentEmail}>
                          o en
                          {' '}
                          <span>{commercialAgent?.email}</span>
                        </IonLabel>
                        <Button
                          className={styles.buttonEmailCommercial}
                          fill="outline"
                          id="button-email-commercial"
                          onClick={() => {
                            handleEmail();
                          }}
                        >
                          <p>Escríbenos</p>
                        </Button>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </div>
              </div>
              <div
                className={classNames(
                  styles.pageContactCardWrapper,
                  styles.operationsCard,
                )}
              >
                <div className={styles.pageContactCardOperations}>
                  <div className={styles.contactContentWrapper}>
                    <div className={styles.contactTitles}>
                      <IonLabel className={styles.contactText}>
                        Contacta con nosotros
                      </IonLabel>
                    </div>

                    <div className={styles.contactContentContainer}>
                      <p>
                        ¿Algún problema? Desde operaciones también estamos
                        disponibles en el teléfono
                        {' '}
                        <span>91 216 12 12</span>
                      </p>
                      <p className={styles.contactTime}>Horario de atención:</p>
                      <ul>
                        <li>Lunes a Viernes de 09:00 a 21:00h</li>
                        <li>Sábados de 10:00 a 21:00h</li>
                      </ul>
                    </div>

                    <div className={styles.contactContentContainer}>
                      <p>
                        Visita
                        {' '}
                        <a href="https://pepperfinance.es">pepperfinance.es</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonPage>
  );
}
